/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import user from 'js/lib/user';

import { InlineNotification, LoadingSection, Pagination, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { submitResponse } from 'bundles/reviews-common/utils/ReviewHelpfulAPIUtils';
import ReviewTableCard, { getReviewAuthorName } from 'bundles/reviews/components/ReviewTableCard';
import ReviewsTableToolbar from 'bundles/reviews/components/ReviewsTableToolbar';
import type {
  AllCourseReviews,
  AllCourseReviewsVariables,
  AllCourseReviews_ProductReviewsV1Resource_reviews_elements,
} from 'bundles/reviews/components/__generated__/AllCourseReviews';
import COURSE_REVIEW_QUERY from 'bundles/reviews/components/queries/AllCourseReviews';

import _t from 'i18n!nls/reviews';

const MAX_START_PLUS_LIMIT = 10000;

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_VALUES = [10, 20, 30, 40, 50];

const styles = {
  root: css`
    padding: var(--cds-spacing-200) 0;
    margin-bottom: var(--cds-spacing-400);
    scroll-margin-top: 132px;
    width: 100%;
  `,
  header: css`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,
  cardList: css`
    margin-bottom: var(--cds-spacing-400);
  `,
  loading: css`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  `,
  pagination: css`
    display: flex;
    margin-top: var(--cds-spacing-400);
  `,
};

type ReviewsTableProps = {
  courseId: string;
};

export type ReviewWithAuthor = AllCourseReviews_ProductReviewsV1Resource_reviews_elements & {
  fullName?: string | null;
  users?: { publicDemographics?: { fullName?: string | null } | null } | null;
};

export function ReviewsTable({ courseId }: ReviewsTableProps) {
  const track = useTracker();
  const tableRef = React.useRef<HTMLElement | null>(null);
  const hasLoadedReviewsRef = React.useRef(false);
  const pendingPageScrollRef = React.useRef<number | null>(null);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [ratingValues, setRatingValues] = React.useState<number[]>([1, 2, 3, 4, 5]);
  const [productCompleted, setProductCompleted] = React.useState<boolean | undefined>(undefined);
  const [sortByHelpfulVotes, setSortByHelpfulVotes] = React.useState(true);

  const executeAction = useFluxibleAction();
  const effectiveUserId = React.useMemo(() => {
    return user.isAuthenticatedUser() ? user.get().id : undefined;
  }, []);

  const startNum = (page - 1) * pageSize;
  const overLimit = startNum + pageSize >= MAX_START_PLUS_LIMIT;
  const start = String(overLimit ? MAX_START_PLUS_LIMIT - pageSize : startNum);

  const variables: AllCourseReviewsVariables = {
    courseId,
    limit: pageSize,
    start,
    ratingValues,
    sortByHelpfulVotes,
  };
  if (productCompleted === true) {
    variables.productCompleted = true;
  }

  const { data, loading, error } = useQuery<AllCourseReviews, AllCourseReviewsVariables>(COURSE_REVIEW_QUERY, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  React.useEffect(() => {
    if (data?.ProductReviewsV1Resource?.reviews != null) {
      hasLoadedReviewsRef.current = true;
    }
  }, [data]);

  React.useEffect(() => {
    if (pendingPageScrollRef.current === page && !loading) {
      tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      pendingPageScrollRef.current = null;
    }
  }, [loading, page]);

  const shouldShowInitialLoadingState = loading && !data && !hasLoadedReviewsRef.current;
  const shouldShowRefetchLoadingState = loading && !data && hasLoadedReviewsRef.current;

  const reviews: ReviewWithAuthor[] =
    data?.ProductReviewsV1Resource?.reviews?.elements?.filter(
      (review): review is NonNullable<typeof review> => review != null && getReviewAuthorName(review) != null
    ) ?? [];
  const total = Number(data?.ProductReviewsV1Resource?.reviews?.paging?.total ?? 0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const cappedPage = overLimit ? totalPages : page;

  const handleLearnerChange = React.useCallback(
    (value: boolean) => {
      track('change_select', {
        select: {
          name: 'reviews_table_filter_learners',
          value: value ? ['completers'] : ['all_learners'],
        },
        product: {
          id: courseId,
        },
      });
      setProductCompleted(value);
      setPage(1);
    },
    [courseId, track]
  );

  const handleStarChange = React.useCallback(
    (value: number[]) => {
      track('change_select', {
        select: {
          name: 'reviews_table_filter_stars',
          value: value.length === 1 ? [`star_${value[0]}`] : ['all_stars'],
        },
        product: {
          id: courseId,
        },
      });
      setRatingValues(value);
      setPage(1);
    },
    [courseId, track]
  );

  const handleSortChange = React.useCallback(
    (helpful: boolean) => {
      track('change_select', {
        select: {
          name: 'reviews_table_sort',
          value: helpful ? ['most_helpful'] : ['most_recent'],
        },
        product: {
          id: courseId,
        },
      });
      setSortByHelpfulVotes(helpful);
      setPage(1);
    },
    [courseId, track]
  );

  const handlePageChange = React.useCallback((_e: React.ChangeEvent<unknown>, newPage: number) => {
    pendingPageScrollRef.current = newPage;
    setPage(newPage);
  }, []);

  const handlePageSizeChange = React.useCallback((_e: React.ChangeEvent<unknown>, newSize: number) => {
    pendingPageScrollRef.current = 1;
    setPageSize(newSize);
    setPage(1);
  }, []);

  const handleMarkHelpful = React.useCallback(
    (reviewId: string, voteType: string) => {
      if (!effectiveUserId || !executeAction) return;
      executeAction(submitResponse, {
        reviewData: { userId: effectiveUserId, reviewId },
        voteType,
        onSuccess: () => {},
      });
    },
    [executeAction, effectiveUserId]
  );

  const pageSizeInputOptions = React.useMemo(
    () =>
      PAGE_SIZE_VALUES.map((value) => ({
        label: _t('#{count} results per page', { count: value }),
        value,
      })),
    []
  );

  const pageSizeInputProps = { label: _t('Show') };

  if (shouldShowInitialLoadingState) {
    return (
      <div css={[styles.root, styles.loading]} data-testid="reviews-table-loading">
        <LoadingSection />
      </div>
    );
  }

  if (error) {
    return (
      <div css={styles.root} data-testid="reviews-table-error">
        <InlineNotification severity="error" title={_t('Unable to load reviews')} children={error.message} />
      </div>
    );
  }

  const displayTotal = total.toLocaleString();
  const showingLabel = _t('Showing: #{count} of #{total}', {
    count: reviews.length,
    total: displayTotal,
  });

  return (
    <section ref={tableRef} css={styles.root} data-testid="reviews-table" aria-busy={loading}>
      <header css={styles.header} data-testid="reviews-table-header">
        <Typography2 variant="subtitleLarge" component="h2">
          {_t('All reviews')}
        </Typography2>
        {!shouldShowRefetchLoadingState && (
          <React.Fragment>
            <Typography2 variant="bodySecondary" color="supportText" component="p">
              {showingLabel}
            </Typography2>
          </React.Fragment>
        )}
      </header>

      <ReviewsTableToolbar
        productCompleted={productCompleted}
        ratingValues={ratingValues}
        sortByHelpfulVotes={sortByHelpfulVotes}
        onLearnerChange={handleLearnerChange}
        onStarChange={handleStarChange}
        onSortChange={handleSortChange}
      />

      {loading ? (
        <div css={styles.loading} data-testid="reviews-table-loading">
          <LoadingSection />
        </div>
      ) : reviews.length === 0 ? (
        <React.Fragment>
          <Typography2 variant="bodyPrimary" color="supportText" data-testid="reviews-table-empty" component="p">
            {_t('No reviews found')}
          </Typography2>
        </React.Fragment>
      ) : (
        <div css={styles.cardList} data-testid="reviews-table-list" data-e2e="reviews-list">
          {reviews.map((review) => (
            <ReviewTableCard
              key={review?.id}
              review={review}
              userId={effectiveUserId}
              onMarkHelpful={handleMarkHelpful}
            />
          ))}
        </div>
      )}

      {!shouldShowRefetchLoadingState && total > 0 && (
        <div css={styles.pagination} data-testid="reviews-table-pagination">
          <Pagination
            total={total}
            page={cappedPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeInputOptions={pageSizeInputOptions}
            pageSizeInputProps={pageSizeInputProps}
            showXsNav
            alignment="start"
          />
        </div>
      )}
    </section>
  );
}

export default ReviewsTable;
